
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/c02c3446a2b3e29ad54a764122882bc0.png" 
                alt="NexBlocX" 
                className="h-40"
              />
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link 
                to="/"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link 
                to="/"
                className="bg-amber-500 text-gray-900 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
              >
                Contact Us
              </Link>
            </div>

            <button 
              className="md:hidden cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-900`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                Home
              </Link>
              <Link 
                to="/"
                className="block w-full text-left px-4 py-2 bg-amber-500 text-gray-900 hover:bg-amber-400 rounded-lg font-semibold cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <div className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-12">Last Updated: 01/06/2026</p>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-8">
              This Privacy Policy describes how <strong>NexBlocX</strong>, operated in partnership with <strong>Polycrete Systems</strong> ("we," "our," or "us"), collects, uses, stores, and protects personal and business information obtained through our website, services, communications, and related platforms (collectively, the "Services").
            </p>
            <p className="text-gray-700 mb-8">
              By accessing or using the Services, you acknowledge that you have read, understood, and agreed to this Privacy Policy.
            </p>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="text-gray-700 mb-4">We may collect the following types of information:</p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">a. Personal Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company name and role</li>
                <li>Mailing or project address</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">b. Business & Project Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Project details and specifications</li>
                <li>Architectural or construction-related documents</li>
                <li>Communication records</li>
                <li>Service requests and inquiries</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">c. Technical & Usage Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>IP address</li>
                <li>Browser type and device information</li>
                <li>Pages visited and interaction data</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Respond to inquiries and provide customer support</li>
                <li>Process and manage project requests</li>
                <li>Communicate updates, promotions, and service-related information</li>
                <li>Improve our website, services, and user experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. SMS and Communication Consent</h2>
              <p className="text-gray-700 mb-4">
                By providing your phone number, you consent to receive SMS messages, phone calls, and emails from NexBlocX related to your inquiries, orders, project updates, and promotional offers.
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Message and data rates may apply</li>
                <li>Message frequency may vary</li>
                <li>Reply <strong>STOP</strong> to opt out of SMS at any time</li>
                <li>Reply <strong>HELP</strong> for assistance</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Sharing of Information</h2>
              <p className="text-gray-700 mb-4">We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Trusted service providers who assist in delivering our services</li>
                <li>Business partners involved in project fulfillment</li>
                <li>Legal authorities when required by law or to protect our rights</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement reasonable administrative, technical, and physical safeguards to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to enhance your experience, analyze site traffic, and personalize content. You may adjust your browser settings to refuse cookies, but some features of the Services may not function properly.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
              <p className="text-gray-700 mb-4">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Access, correct, or delete your personal information</li>
                <li>Opt out of marketing communications</li>
                <li>Request data portability</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, please contact us at <strong>info@nexblocx.com</strong>.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
              <p className="text-gray-700 mb-4">
                Our website may contain links to third-party sites. We are not responsible for the privacy practices or content of those sites. We encourage you to review their privacy policies before providing any information.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Your continued use of the Services after any changes constitutes acceptance of the revised policy.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p className="text-gray-700">
                <strong>NexBlocX</strong><br />
                Email: info@nexblocx.com<br />
                Phone: 561-918-2055
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-gray-900 text-white py-16 overflow-hidden min-h-[400px]">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/5f47c77c836e004333a2d82b946d5e21.jpeg" 
            alt="Footer Background" 
            className="w-full h-full object-cover object-center absolute inset-0"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4 md:mb-0 text-sm text-gray-400">
                <span>All rights reserved 2026 NexblocX Polycrete Systems</span>
                <span className="hidden md:inline">|</span>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span>|</span>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Powered by Readdy
                </a>
              </div>

              <div className="flex items-center space-x-4">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="X">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
